import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
} from '@angular/core';
import * as d3 from 'd3';

import {
  LegendDirective,
  PieChartConfig,
  PieChartData,
  ResizeObserverDirective,
  defaultConfigPie,
} from '@bookly/shared';

@Component({
  selector: 'bsh-pie-chart',
  imports: [ResizeObserverDirective, LegendDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './pie-chart.component.html',
  styleUrl: './pie-chart.component.scss',
})
export class PieChartComponent implements OnInit {
  @Input() set data(val: PieChartData[]) {
    this.chartData = val ?? [];
    this.updateChart();
  }
  @Input() set config(val: PieChartConfig) {
    this._config = { ...defaultConfigPie, ...val };
    this.updateChart();
  }

  protected chartData: PieChartData[] = [];
  protected _config = { ...defaultConfigPie };
  protected width = 0;
  protected height = 0;
  protected renderMargin = { ...defaultConfigPie.margin };
  protected radius = 0;
  protected arcs: d3.PieArcDatum<PieChartData>[] = [];
  protected colorScale!: d3.ScaleOrdinal<string, string>;
  protected subgroups: string[] = [];
  protected legendWidth = 120;

  ngOnInit() {
    this.colorScale = d3
      .scaleOrdinal<string, string>()
      .domain(this.chartData.map(d => d.label))
      .range(this._config.colors);
  }

  /**
   * Handles resize events and updates chart dimensions.
   * @param {DOMRectReadOnly} evt The resize event.
   */
  protected observeResize(evt: DOMRectReadOnly) {
    this.width = evt.width;
    this.height = evt.height;
    this.updateChart();
  }

  /**
   * Updates pie chart layout, radius, color scale, and arc generator based on current data and config.
   * Called internally when data, config, or dimensions change.
   * @private
   */
  private updateChart() {
    if (!this.chartData?.length || this.width === 0 || this.height === 0)
      return;
    this.renderMargin = { ...this._config.margin };
    this.subgroups = this.chartData.map(d => `${d.label} (${d.value})`);
    const { left, right, top, bottom } = this.renderMargin;
    const w = Math.max(this.width - left - right, 0);
    const h = Math.max(this.height - top - bottom, 0);
    this.radius = Math.min(w, h) / 2;

    this.colorScale = d3
      .scaleOrdinal<string, string>()
      .domain(this.chartData.map(d => d.label))
      .range(this._config.colors);

    const pie = d3
      .pie<PieChartData>()
      .sort(null)
      .value(d => d.value);

    this.arcs = pie(this.chartData);
  }

  /**
   * Returns the SVG path string for a given pie arc.
   * @param {d3.PieArcDatum<PieChartData>} d The arc data.
   * @returns {string} The SVG path for the arc.
   */
  protected arcPath(d: d3.PieArcDatum<PieChartData>): string {
    const arc = d3
      .arc<d3.PieArcDatum<PieChartData>>()
      .innerRadius(0)
      .outerRadius(this.radius);
    return arc(d) ?? '';
  }

  /**
   * Returns the fill color for a given pie arc.
   * @param {d3.PieArcDatum<PieChartData>} d The arc data.
   * @returns {string} The color string.
   */
  protected arcFill(d: d3.PieArcDatum<PieChartData>): string {
    return this.colorScale(d.data.label);
  }

  /**
   * Calculates the label position for a given pie arc.
   * @param {d3.PieArcDatum<PieChartData>} d The arc data.
   * @returns {{x: number, y: number}} The label coordinates.
   */
  protected labelPos(d: d3.PieArcDatum<PieChartData>): {
    x: number;
    y: number;
  } {
    const arc = d3
      .arc<d3.PieArcDatum<PieChartData>>()
      .innerRadius(this.radius * 0.7)
      .outerRadius(this.radius * 0.7);
    const [x, y] = arc.centroid(d);
    return { x, y };
  }
}
